import { format } from "date-fns";
import { useMemo } from "react";
import { dailyMealsPath, menuPath } from "../../functions/src/admin/paths";
import { dailyMealsMap, menuMap } from "../../functions/src/api/dbConfig";
import { isMealAvailableForDate } from "../../functions/src/common/mealAvailability";
import { Lookup, Meal } from "../../functions/src/types";
import { currentDailyMeals, currentMenu, DataSnapshot, getAdminSchoolKey } from "./firebase";
import { useDatabase, useList } from "./firebaseHooks";
import { getLangValue, Language } from "./Lang";

/**
 * Helper function to get meal data from either traditional meals or daily meals
 * @param mealKey - The meal key to look up
 * @param sFoods - Traditional meals snapshot
 * @param dailyMealsList - Daily meals list
 * @returns The meal object or null if not found
 */
export function getMealData(mealKey: string, sFoods: DataSnapshot<Lookup<any>>, dailyMealsList: DataSnapshot<Meal>[], menuData?: { [key: string]: Meal }): Meal | null {
    if (!mealKey) return null;

    // First try to find the meal in menu data (for package exchanges)
    if (menuData && menuData[mealKey]) {
        return menuData[mealKey];
    }

    // If not found in menu data, look in daily meals
    const dailyMeal = dailyMealsList?.find(dm => dm.key === mealKey)?.val();
    if (dailyMeal) {
        return dailyMeal;
    }

    // If not found in daily meals, look in traditional meals as fallback
    const traditionalMeal = sFoods?.child(mealKey)?.val();
    return traditionalMeal || null;
}

/**
 * Helper function to get meal name from either traditional meals or daily meals
 * @param mealKey - The meal key to look up
 * @param sFoods - Traditional meals snapshot
 * @param dailyMealsList - Daily meals list
 * @param lang - Language code ('el' or 'en')
 * @param menuData - Menu data object
 * @returns The meal name or null if not found
 */
export function getMealName(mealKey: string, sFoods: DataSnapshot<Lookup<any>>, dailyMealsList: DataSnapshot<Meal>[], lang: Language, menuData?: { [key: string]: Meal }): string | null {
    if (!mealKey) return null;

    // First try to get name from menu data (for package exchanges)
    if (menuData && menuData[mealKey]?.name) {
        return getLangValue(menuData[mealKey].name, lang);
    }

    // If not found in menu data, look in daily meals
    const dailyMeal = dailyMealsList?.find(dm => dm.key === mealKey);

    const dailyMealName = getLangValue(dailyMeal?.val()?.name, lang);
    if (dailyMealName) {
        return dailyMealName;
    }

    // If not found in daily meals, look in traditional meals as fallback
    const traditionalMealName = sFoods?.child(mealKey)?.val()?.name;
    return getLangValue(traditionalMealName, lang);
}

/**
 * Hook to get daily meals list for the current school
 * @returns Array of daily meals DataSnapshots
 */
export function useDailyMealsList(): DataSnapshot<Meal>[] {
    const currentDailyMeals = dailyMealsMap[getAdminSchoolKey()];
    const sDailyMeals = useDatabase(dailyMealsPath(currentDailyMeals), ref => ref.orderByChild('order'));
    return useList(sDailyMeals) || [];
}

/**
 * Helper function to determine if portion size should be displayed
 * Hides portion size only when there's exactly one portion size available and it's the large/normal portion
 * @param meal - The meal object to check
 * @param combosData - Optional: combos data for combo price checking
 * @param hasSmall - Optional: whether small portion is available (overrides meal.priceSmall check)
 * @param hasMedium - Optional: whether medium portion is available (overrides meal.priceMedium check)
 * @param hasLarge - Optional: whether large portion is available (overrides meal.price check)
 * @returns boolean indicating if portion size should be shown
 */
export function shouldShowPortionSize(
    meal: Meal | null,
    combosData?: any,
    hasSmall?: boolean,
    hasMedium?: boolean,
    hasLarge?: boolean
): boolean {
    // If specific portion availability flags are provided, use those
    if (hasSmall !== undefined || hasMedium !== undefined || hasLarge !== undefined) {
        const small = hasSmall ?? false;
        const medium = hasMedium ?? false;
        const large = hasLarge ?? false;

        // Count how many portion sizes are available
        const portionCount = [small, medium, large].filter(Boolean).length;

        // Show portion size unless there's only one portion size and it's large
        return !(portionCount === 1 && large);
    }

    // Fallback to meal object if no specific flags provided
    if (!meal) return false;

    // Check if we should use combo prices (same logic as MealsPopup.tsx)
    const shouldUseComboPrice = meal.courseType === 'main' && combosData;
    const preferGlobalComboPrices = combosData?.prices?.preferGlobalComboPrices ?? false;

    const hasSmallFromMeal = shouldUseComboPrice ?
        (preferGlobalComboPrices || !meal?.priceComboSmall ? (!!combosData?.prices?.priceSmall && combosData.prices.priceSmall > 0) : (!!meal?.priceComboSmall && meal?.priceComboSmall > 0)) :
        (!!meal?.priceSmall && meal?.priceSmall > 0);
    const hasMediumFromMeal = shouldUseComboPrice ?
        (preferGlobalComboPrices || !meal?.priceComboMedium ? (!!combosData?.prices?.priceMedium && combosData.prices.priceMedium > 0) : (!!meal?.priceComboMedium && meal?.priceComboMedium > 0)) :
        (!!meal?.priceMedium && meal?.priceMedium > 0);
    const hasLargeFromMeal = shouldUseComboPrice ?
        (preferGlobalComboPrices || !meal?.priceComboLarge ? (!!combosData?.prices?.priceLarge && combosData.prices.priceLarge > 0) : (!!meal?.priceComboLarge && meal?.priceComboLarge > 0)) :
        (!!meal?.price && meal?.price > 0);

    // Count how many portion sizes are available
    const portionCount = [hasSmallFromMeal, hasMediumFromMeal, hasLargeFromMeal].filter(Boolean).length;

    // Show portion size unless there's only one portion size and it's large
    return !(portionCount === 1 && hasLargeFromMeal);
}

/**
 * Hook to get menu data for a specific date
 * @param date - The date to get menu data for
 * @param menuKey - The menu key (optional, defaults to current menu)
 * @returns Object with menuData and menuItems
 */
export function useMenuData(date: string, menuKey?: string): { menuData: { [key: string]: Meal }, menuItems: DataSnapshot<Meal>[] } {
    const currentMenuKey = menuKey || menuMap[getAdminSchoolKey()] || currentMenu;
    const menusSnapshot = useDatabase(`${menuPath(currentMenuKey)}/${date}`);
    const menuItems = useList(menusSnapshot) || [];

    const menuData: { [key: string]: Meal } = {};

    if (menuItems) {
        menuItems.forEach((menuItemSnapshot) => {
            const menuItem = menuItemSnapshot.val();
            if (menuItem?.key) {
                menuData[menuItem.key] = menuItem;
            }
        });
    }

    return { menuData, menuItems };
}

/**
 * Hook to check if there are multiple exchangeable meals available
 * Uses the same logic as Order.tsx to determine meal availability
 * @param date - Optional date to check (defaults to today)
 * @returns boolean indicating if there are multiple exchangeable meals
 */
export function useHasMultipleExchangeableMeals(date?: string): boolean {
    const checkDate = date || format(new Date(), 'yyyy_MM_dd');

    // Daily meals (all other courseTypes) from dailyMeals path
    const sDailyMeals = useDatabase(dailyMealsPath(currentDailyMeals), ref => ref.orderByChild('order').startAt(0));
    const dailyMealsList: DataSnapshot<Meal>[] = useList(sDailyMeals) || [];

    // Get menu items for the date to check menu availability
    const menusSnapshot = useDatabase(`${menuPath(currentMenu)}/${checkDate}`);
    const menuItems = useList(menusSnapshot);

    // Count available exchangeable meals (menu meals + daily meals with exchange enabled) - same logic as Order.tsx
    const availableExchangeableMeals = useMemo(() => {
        let count = menuItems?.filter(menuItem => menuItem.val().courseType === 'main').length ?? 0;

        // Count daily meals that have exchange enabled AND are available for the current date
        if (dailyMealsList) {
            count += dailyMealsList.filter(dailyMeal => {
                const dailyMealData = dailyMeal.val();
                return dailyMealData &&
                    dailyMealData.isExchangable === true &&
                    isMealAvailableForDate(dailyMealData, checkDate);
            }).length;
        }

        return count;
    }, [menuItems, dailyMealsList, checkDate]);

    return availableExchangeableMeals > 1;
}
